/**
 * useChangeRequests — fetches pending change requests visible to the
 * current Orchist (incoming + outgoing).
 *
 * Per the Change Doctrine (sacred/change-doctrine.md):
 *   - Incoming = requests where YOU are the requestee (your consent
 *     is needed). Show with accept / reject UI.
 *   - Outgoing = requests YOU created. Shown for visibility; the
 *     requester can cancel them.
 *
 * RLS limits visibility to requests the caller is a party to.
 * Status filter: only 'pending' requests for active workflow display.
 * Resolved / rejected / cancelled / auto-accepted requests are still
 * in the DB for audit (visible to the parties on demand).
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { parseUTCTimestamp } from '@/lib/dates';

export type ChangeRequestField =
  | 'invitationSentAt'
  | 'invitationProposedAcceptedAt'
  | 'invitationAcceptedAt';

export type ChangeRequestStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'auto_accepted'
  | 'cancelled';

export interface ChangeRequestRow {
  id: string;
  contactId: string;
  requesterContactId: string;
  requesteeContactId: string;
  field: ChangeRequestField;
  currentValue: string | null;
  proposedValue: string | null;
  reason: string | null;
  status: ChangeRequestStatus;
  requestedAt: string;
  respondedAt: string | null;
  expiresAt: string;
}

const REQUEST_COLUMNS =
  'id, contactId, requesterContactId, requesteeContactId, field, currentValue, proposedValue, reason, status, requestedAt, respondedAt, expiresAt';

export function useChangeRequests() {
  const { contact } = useAuth();
  const [incoming, setIncoming] = useState<ChangeRequestRow[]>([]);
  const [outgoing, setOutgoing] = useState<ChangeRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!contact) {
      setIncoming([]);
      setOutgoing([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .schema('core')
        .from('ContactChangeRequest')
        .select(REQUEST_COLUMNS)
        .eq('status', 'pending')
        .order('requestedAt', { ascending: false });

      if (cancelled) return;
      if (error) {
        setIncoming([]);
        setOutgoing([]);
        setLoading(false);
        return;
      }

      const all = (data ?? []) as ChangeRequestRow[];
      setIncoming(all.filter((r) => r.requesteeContactId === contact.id));
      setOutgoing(all.filter((r) => r.requesterContactId === contact.id));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [contact, refreshKey]);

  return { incoming, outgoing, loading, refresh };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function fieldHumanLabel(field: ChangeRequestField): string {
  switch (field) {
    case 'invitationSentAt':
      return 'when the invitation was extended';
    case 'invitationProposedAcceptedAt':
      return 'when the inviter believes acknowledgment happened';
    case 'invitationAcceptedAt':
      return 'when the recognition was remembered';
  }
}

/**
 * Human-readable relative-time helper.
 *   "in 30 days" / "in 5 days" / "today" / "tomorrow" / "yesterday"
 */
export function relativeTime(iso: string): string {
  const target = parseUTCTimestamp(iso).getTime();
  if (isNaN(target)) return '';
  const now = Date.now();
  const days = Math.round((target - now) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0 && days <= 14) return `in ${days} days`;
  if (days > 14) return `in ~${Math.round(days / 7)} weeks`;
  if (days >= -14) return `${Math.abs(days)} days ago`;
  return `~${Math.round(Math.abs(days) / 7)} weeks ago`;
}
